import React, { useState, useEffect, useMemo } from "react";
import styled from "styled-components";
import { MdPersonAddAlt1, MdClose } from "react-icons/md";
import { useSelector, useDispatch } from "react-redux";
import * as Actions from "../../dashboard/store/actions/kube.actions";
import { toggleSnackbar } from "../../dashboard/store/actions/kube.actions";

// Styled Components
const Wrapper = styled.div`
  position: fixed;
  inset: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  background: rgba(0, 0, 0, 0.5);
  transition: opacity 0.25s ease;
  opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};
  z-index: ${({ isVisible }) => (isVisible ? 1 : -1)};
`;

const Card = styled.div`
  width: 25rem;
  background: #ecf0f3;
  border-radius: 10px;
  text-align: center;
  box-shadow: 0 4px 15px -5px black;
  transition: opacity 0.25s ease;
  opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};
  padding-bottom: 1.5rem;
  position: relative;
`;


const Title = styled.h3`
  margin: 3rem 0 2rem;
  font-family: Inter, sans-serif;
  font-weight: 700;
  font-size: 1.25rem;
  color: #4b566c;
`;

const CloseButton = styled.div`
  position: absolute;
  right: 1rem;
  top: 1rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  font-size: 1.25rem;
  background: rgba(255, 255, 255, 0.8);
  color: #f3795e;
  transition: all 0.2s ease;

  &:hover {
    background: #f3795e;
    color: white;
    transform: scale(1.05);
  }
`;

const InputField = styled.input`
  width: 18rem;
  height: 2.5rem;
  margin-top: 0.5rem;
  padding: 0.25rem 1rem;
  font-family: "Inter";
  font-weight: bold;
  font-size: 1rem;
  border-radius: 10px;
  border: 1px solid #f3795e;
  background: rgba(243, 121, 94, 0.2);
  outline: none;

  &:focus {
    background: #fff;
    border-color: black;
  }
`;

const Select = styled.select`
  width: 18rem;
  height: 2.5rem;
  margin-top: 1rem;
  border-radius: 10px;
  border: 1px solid #f3795e;
  font-size: 1rem;
  padding: 0.25rem 0.7rem;
  background: rgba(243, 121, 94, 0.2);
`;

const Button = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 3rem;
  height: 3rem;
  margin: 2rem auto 0;
  font-size: 1.3rem;
  border: none;
  border-radius: 50%;
  color: #fff8f0;
  background: #f3795e;
  cursor: pointer;
  transition: all 0.25s ease;

  &:hover {
    background: #e24623;
    box-shadow: inset 0px 4px 4px rgba(0, 0, 0, 0.25);
  }
`;

const ErrorMsg = styled.p`
  color: red;
  font-size: 0.75rem;
  margin: 0.25rem 0 0.5rem;
`;

// =================== Component ===================
const OverlayCard = ({ isVisible, setShowOverlayCard }) => {
  const dispatch = useDispatch();
  const authEmail = useSelector((state) => state.loginUser.LoggedInUser?.attributes?.email);
  const response = useSelector((state) => state.workspaces.setUsers);

  const [user, setUser] = useState({
    name: "",
    email: "",
    org: "",
    group: "",
    role: "",
    timezone: "",
    country: "",
    managerEmail: "",
    type: "",
    hoursPerWeek: "",
  });

  const [formErrors, setFormErrors] = useState({});
  const [isSubmit, setIsSubmit] = useState(false);

  // ============ Handlers ============
  const handleInput = (e) => {
    const { name, value } = e.target;

    // Auto-update hoursPerWeek based on type
    if (name === "type") {
      let defaultHours = 20;
      if (value === "Full Time") defaultHours = 40;
      setUser((prev) => ({ ...prev, type: value, hoursPerWeek: defaultHours }));
    } else {
      setUser((prev) => ({ ...prev, [name]: value }));
    }
  };

  const validate = (values) => {
    const required = [
      "name",
      "email",
      "org",
      "group",
      "role",
      "timezone",
      "country",
      "managerEmail",
      "type",
      "hoursPerWeek",
    ];
    return required.reduce((acc, field) => {
      if (!values[field]) acc[field] = `${field.charAt(0).toUpperCase() + field.slice(1)} is required`;
      return acc;
    }, {});
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const errors = validate(user);
    setFormErrors(errors);
    setIsSubmit(true);

    if (!Object.keys(errors).length && authEmail) {
      dispatch(
        Actions.getAddUsers(
          authEmail,
          user.name,
          user.email,
          user.org,
          user.group,
          user.role,
          user.timezone,
          user.country,
          user.managerEmail,
          user.type,
          user.hoursPerWeek
        )
      );

      setUser({
        name: "",
        email: "",
        org: "",
        group: "",
        role: "",
        timezone: "",
        country: "",
        managerEmail: "",
        type: "",
        hoursPerWeek: "",
      });
    }
  };

  // ============ Side Effects ============
  useEffect(() => {
    if (isSubmit && response) {
      dispatch(
        toggleSnackbar({
          status: true,
          message: response,
          severity: response === "User added successfully" ? "success" : "error",
        })
      );
      setIsSubmit(false);
    }
  }, [response]);

  // ============ Select Options ============
  const options = useMemo(
    () => ({
      org: [{ value: "5e9706c43ee0632bad80b887", label: "Readyly" }],
      group: ["Engineering", "Business"],
      role: ["User", "Admin", "Manager", "Superadmin"],
      timezone: ["IST", "EST", "PH"],
      country: ["India", "USA", "Philippines"],
      type: ["Full Time", "Part Time", "Intern"],
    }),
    []
  );

  return (
    <Wrapper isVisible={isVisible}>
      <Card isVisible={isVisible} >
        <CloseButton onClick={() => setShowOverlayCard(false)}>
          <MdClose />
        </CloseButton>

        <Title>User Details</Title>

        <form onSubmit={handleSubmit}>
          {/* Organization */}
          <Select name="org" value={user.org} onChange={handleInput}>
            <option hidden value="">
              Select Organization
            </option>
            {options.org.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
          <ErrorMsg>{formErrors.org}</ErrorMsg>

          {/* Text Inputs */}
          {["name", "email", "managerEmail"].map((field) => (
            <div key={field}>
              <InputField
                type="text"
                placeholder={
                  field === "managerEmail"
                    ? "Manager's Email"
                    : field.charAt(0).toUpperCase() + field.slice(1)
                }
                name={field}
                value={user[field]}
                onChange={handleInput}
              />
              <ErrorMsg>{formErrors[field]}</ErrorMsg>
            </div>
          ))}

          {/* Select Fields */}
          {["group", "role", "type", "timezone", "country"].map((field) => (
            <div key={field}>
              <Select name={field} value={user[field]} onChange={handleInput}>
                <option hidden value="">
                  Select {field.charAt(0).toUpperCase() + field.slice(1)}
                </option>
                {options[field].map((val) => (
                  <option key={val} value={val}>
                    {val}
                  </option>
                ))}
              </Select>
              <ErrorMsg>{formErrors[field]}</ErrorMsg>
            </div>
          ))}

          {/* Hours Per Week */}
          <div>
            <InputField
              type="number"
              placeholder="Working Hours/Week"
              name="hoursPerWeek"
              value={user.hoursPerWeek}
              onChange={handleInput}
              min="1"
              max="80"
            />
            <ErrorMsg>{formErrors.hoursPerWeek}</ErrorMsg>
          </div>

          <Button type="submit">
            <MdPersonAddAlt1 />
          </Button>
        </form>
      </Card>
    </Wrapper>
  );
};

export default OverlayCard;